/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import {useMutation} from 'react-query';
import {loadSCIDevicesBySerialNumber} from '@ion/core/src/services/Connect';
import {Device, GenericObject} from '@ion/core/src/types/entities';
import {createMutationResult} from '../utils/helpers';

export interface UseLoadSCIDevicesParams {
  accountId: number;
  identifiers: string[];
  SCIDeviceType: GenericObject;
};

export type UseLoadSCIDevicesResult = {
  device: Device;
  loadSCIDevices: (params: UseLoadSCIDevicesParams, callbacks) => void;
}

/**
 * The UseLoadSCIDevices hook is used to retrieve device bu identifiers.
 *
 * ```typescript
 *
 *  import {UseLoadSCIDevices} from '@ion/packages/query/src/hooks';
 *
 *  const {device} = UseLoadSCIDevices({
 *    accountId,
 *    identifiers,
 *    SCIDeviceType
 *  });
 *
 * ```
 */
export const useLoadSCIDevices = (): UseLoadSCIDevicesResult => {
  const mutation = useMutation(async(params: UseLoadSCIDevicesParams) => {
    const {
      accountId,
      identifiers,
      SCIDeviceType
    } = params;
    const result = await loadSCIDevicesBySerialNumber(identifiers, SCIDeviceType, accountId);
    if (result) {
      return result;
    }
    return '';
  });

  return createMutationResult(
    mutation, {
      loadSCIDevices : mutation.mutate
    }
  ) as UseLoadSCIDevicesResult;
};

export default useLoadSCIDevices;
